import { useState, useEffect, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWifiSlash } from '@fortawesome/pro-regular-svg-icons'
import { Link } from 'react-router-dom'
import currency from 'currency.js'

import AuthContext from '../../../context/AuthContext'
import usePrivateApi from '../../../hooks/usePrivateApi'
import SubdealerInviteModal from './ModalInviteSubdealer'
import Button from '../../../components/Button'
import SubdealerModalAddPackage from './SubdealerModalAddPackage'
import SubdealerModalEditPackage from './SubdealerModalEditPackage'
import SubdealerModalDeletePackage from './SubdealerModalDeletePackage'

const SubdealerCreditPackages = ({ match }) => {
  const { dealerId } = match.params
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [auth] = useContext(AuthContext)
  const [subdealer, setSubdealer] = useState(null)
  const [newModalOpen, setNewModalOpen] = useState(false)
  const [editPackageId, setEditPackageId] = useState(null)
  const [deletePackageId, setDeletePackageId] = useState(null)

  const [{ res, success, error }, { sendRequest, resetSuccess }] =
    usePrivateApi()

  useEffect(() => {
    sendRequest({ url: `/settings/subdealers/${dealerId}` })
  }, [sendRequest, dealerId])

  useEffect(() => {
    if (success) {
      setSubdealer(res.data.subdealer)
      resetSuccess()
    }
  }, [success, resetSuccess, res, auth])

  const handleCreditPackageSuccess = (creditPackages) => {
    setSubdealer({
      ...subdealer,
      credit: {
        ...subdealer.credit,
        packages: creditPackages,
      },
    })
    setNewModalOpen(false)
    setEditPackageId(null)
    setDeletePackageId(null)
  }

  if (error) {
    return (
      <div className="flex items-center justify-center flex-1 flex-col p-18">
        <FontAwesomeIcon
          icon={faWifiSlash}
          className="text-gray-600"
          size="2x"
        />
        <h3 className="text-xl font-semibold tracking-tighter mt-4">
          Could not retrieve subdealer details
        </h3>
        <p className="text-sm text-gray-600 mt-1 mb-6">
          Please refresh the page to try again.
        </p>
        <Button color="white" onClick={() => window.location.reload()}>
          Refresh page
        </Button>
      </div>
    )
  }

  if (subdealer === null) {
    return (
      <div className="flex items-center justify-center p-12 flex-1">
        <span className="spinner" />
      </div>
    )
  }

  return (
    <div className="px-6 pt-8 pb-12 space-y-4 sm:space-y-0 sm:flex sm:space-x-8 sm:items-start">
      <div className="max-w-sm flex-1">
        <h2 className="text-lg font-semibold tracking-tight mb-2">
          {subdealer.name} ({subdealer.tgtNumber})
        </h2>
        <div className="text-gray-600 text-sm space-y-2 max-w-prose">
          <p>
            You are managing the credit packages that this subdealer can buy
            from you using dealer checkout.
          </p>
        </div>
      </div>
      <div className="flex-1">
        {subdealer.credit.packages.length === 0 ? (
          <>
            <div className="mb-2 bg-gray-50 border border-gray-100 rounded-md px-4 py-3">
              <p className="text-gray-600 text-sm">
                This subdealer has no credit packages
              </p>
            </div>
            <Button
              color="primary"
              className="mt-4"
              onClick={() => setNewModalOpen(true)}
            >
              Add credit package
            </Button>
          </>
        ) : (
          <div className="space-y-2">
            {subdealer.credit.packages
              .sort((a, b) => a.credits - b.credits)
              .map((creditPackage) => (
                <div
                  className="flex justify-between rounded-md bg-gray-100 px-4 py-3"
                  key={creditPackage.name}
                >
                  <div className="flex-1">
                    <h3 className="text-gray-900 font-medium text-sm">
                      {creditPackage.name}{' '}
                      {creditPackage.custom ? '(custom)' : ''}
                    </h3>
                    <p className="text-sm text-gray-700">
                      Total: £{currency(creditPackage.cost / 100).format()}{' '}
                      &middot; Price per file: £
                      {currency(creditPackage.cost / 100)
                        .divide(creditPackage.credits)
                        .format()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      color="white"
                      size="xs"
                      onClick={() => setDeletePackageId(creditPackage.id)}
                    >
                      Delete
                    </Button>
                    <Button
                      color="white"
                      size="xs"
                      onClick={() => setEditPackageId(creditPackage.id)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            <div className="pt-2 flex space-x-2 items-center">
              <Button color="primary" onClick={() => setNewModalOpen(true)}>
                Add credit package
              </Button>
              <Button color="white" as={Link} to="/settings/subdealers">
                Back
              </Button>
            </div>
          </div>
        )}
      </div>
      <SubdealerInviteModal
        isOpen={inviteModalOpen}
        setIsOpen={() => setInviteModalOpen(false)}
      />
      <SubdealerModalAddPackage
        isOpen={newModalOpen}
        setIsOpen={setNewModalOpen}
        subdealerId={subdealer.id}
        onSuccess={handleCreditPackageSuccess}
      />
      <SubdealerModalEditPackage
        isOpen={!!editPackageId}
        setIsOpen={setEditPackageId}
        subdealerId={subdealer.id}
        onSuccess={handleCreditPackageSuccess}
        creditPackage={
          editPackageId &&
          subdealer.credit.packages.find((x) => x.id === editPackageId)
        }
      />
      <SubdealerModalDeletePackage
        isOpen={!!deletePackageId}
        setIsOpen={setDeletePackageId}
        subdealerId={subdealer.id}
        onSuccess={handleCreditPackageSuccess}
        creditPackage={
          deletePackageId &&
          subdealer.credit.packages.find((x) => x.id === deletePackageId)
        }
      />
    </div>
  )
}

export default SubdealerCreditPackages
